import type { JSX } from "react";
// packages
import React from "react";

// components
import { Heading } from "@/shared/components/ui/Heading";
import { Badge } from "@/shared/components/ui/Badge";
import { Switch } from "@/shared/components/ui/switch";
import { Label } from "../ui/label";
import SectionGrid, { SectionGridItem } from "./SectionGrid";
import PreventiveGrid from "./PreventiveGrid";
import { PreventiveToggleButton } from "./PreventiveToggleButton";

// utils
import { BreedUtils } from "@/shared/utils/BreedUtils";
import { PolicyUtils } from "@/shared/utils/PolicyUtils";
import { PreventiveCoverageType } from "@/shared/utils/CoverageUtils";
import kebabCase from "lodash/kebabCase";
import { cn } from "@/shared/utils";

// media
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

// types
import { Card, CardHeader, CardContent, CardFooter } from "../ui/Card";
import { Pet } from "@/shared/types/Quote.interface";
import { IconGridItem } from "@/shared/types/IconGrid.interface";
import { useThemeContext } from "@/shared/contexts/ThemeProvider";
import { PlatinumRecommendationBanner } from "../PlatinumRecommendationBanner";
import { IconUtils } from "@/shared/utils/IconUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { faDiamond } from "@fortawesome/pro-solid-svg-icons";

interface PreventiveCardProps {
    pet: Pet;
    price?: number;
    label: string;
    image: JSX.Element;
    benefits: IconGridItem[] | SectionGridItem[];
    currentWellnessLevel: string;
    wellnessLevel: PreventiveCoverageType;
    onChange: (coverageLevel: PreventiveCoverageType) => void;
    sellingPoints?: string[];
    totalBenefitsValue?: number;
    showPopularAge?: boolean;
    columnClasses?: string;
    headerClasses?: string;
    categorizePreventive?: boolean;
    showPlatinumBanner?: boolean;
    showClickableCards?: boolean;
}

// preventive recs
const BREED_NICKNAMES: Record<string, string> = {
    "French Bulldog": "Frenchie",
    "Labrador Retriever": "Labrador",
    "Golden Retriever": "Golden"
};
const getBreedDisplayName = (breed?: string) => {
    if (!breed) return "";
    // Generic size mixes resolve by shape, not exact name — state catalogs spell them
    // differently (ENG-17827: CA says "lbs.", national says "pounds").
    return BREED_NICKNAMES[breed] ?? BreedUtils.getGenericMixLabel(breed) ?? breed;
};

function PreventiveCard(props: PreventiveCardProps) {
    const {
        pet,
        price,
        label,
        benefits,
        image,
        wellnessLevel,
        sellingPoints,
        showPopularAge,
        totalBenefitsValue,
        currentWellnessLevel,
        onChange,
        columnClasses,
        headerClasses,
        categorizePreventive,
        showPlatinumBanner,
        showClickableCards
    } = props;
    const isActive = wellnessLevel === currentWellnessLevel;
    const { showRebrandTheme } = useThemeContext().theme;
    const isGold = wellnessLevel === "wellness-gold";
    const isPlatinum = wellnessLevel === "wellness-platinum";

    const paddingStyles = {
        "px-4 py-3 sm:p-4 md:px-4 md:py-3 lg:p-4": categorizePreventive,
        "px-1 py-2 xs:px-2 sm:p-4 sm:px-5 sm:py-4 md:px-3 md:py-2 lg:px-5 lg:py-4": !categorizePreventive
    };

    const footerStyles = {
        "bg-background-prevcare border-stroke-prevcare": isPlatinum,
        "bg-background-prevcare-b border-stroke-warning": isGold,
        "bg-background-brand-b-secondary border-stroke-secondary-invert": isPlatinum && !!showRebrandTheme
    };

    // clickable cards tests
    const clickableCardsStyle = {
        "bg-background-prevcare border-b-stroke-prevcare border-b-[3px]": isPlatinum,
        "bg-background-warning border-b-stroke-prevcare-b-secondary border-b-[3px]": isGold
    };

    const hasBreedRules = IconUtils.hasBreedPreventiveRules(pet.breedName);
    const shouldShowRecommendation = hasBreedRules && isPlatinum;

    // clickable cards: the whole card toggles the coverage
    const handleToggle = () => onChange(isActive ? "None" : wellnessLevel);
    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // let nested controls (e.g. the platinum upgrade link) handle their own clicks
        if ((e.target as HTMLElement).closest("button, a")) return;
        handleToggle();
    };
    const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // only respond when the card itself is focused, not a nested control
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
        }
    };

    const priceLabel = typeof price === "number" && (
        <span data-testid={`preventive-price-${kebabCase(label)}`} className="font-bold text-content-primary">
            {UIUtils.formatCurrency(price)}/mo
        </span>
    );
    const priceLabelExperiment = typeof price === "number" && (
        <div data-testid={`preventive-price-${kebabCase(label)}`} className="flex items-center text-content-primary">
            <span className="text-lg font-bold">{UIUtils.formatCurrency(price)}</span>
            <span className="text-sm font-normal">/mo</span>
        </div>
    );

    const addControl = (
        <div className="flex items-center gap-2">
            <Label htmlFor={`preventive-control-${kebabCase(label)}`} className="text-sm font-medium">
                Add {label}
            </Label>
            <Switch
                id={`preventive-control-${kebabCase(label)}`}
                size="lg"
                checked={isActive}
                onCheckedChange={checked => onChange(checked ? wellnessLevel : "None")}
                tabIndex={0}
                role="switch"
                aria-checked={isActive}
            />
        </div>
    );

    const RecommendationBadge = (
        <div className="-mb-5 ml-3 mt-5 flex items-center gap-1 sm:-mb-0 sm:ml-0 sm:mt-0 md:-mb-5 md:ml-5 md:mt-5 lg:-mb-0 lg:ml-0 lg:mt-0">
            <p className="whitespace-nowrap text-xs font-bold leading-4 text-content-success">Most Popular for</p>

            <Badge variant="secondary" className="w-fit py-1 text-xs font-medium leading-4">
                {`${getBreedDisplayName(pet.breedName)} ${PolicyUtils.recommendedPreventiveText(pet.species, pet.age as number).replace(/^(dogs|cats)\s+/i, "")}`}
                <sup className="ml-[1px]">2</sup>
            </Badge>
        </div>
    );

    return (
        <Card
            className={cn(
                `h-full overflow-hidden rounded-3xl bg-background-primary shadow-md`,
                showClickableCards && `group/card cursor-pointer outline-4 -outline-offset-4 outline-stroke-brand-b`,
                // selected: border always on; unselected: only on hover
                showClickableCards && (isActive ? `outline` : `hover:outline`)
            )}
            role={showClickableCards ? "button" : undefined}
            tabIndex={showClickableCards ? 0 : undefined}
            aria-pressed={showClickableCards ? isActive : undefined}
            aria-label={showClickableCards ? (isActive ? `Remove ${label}` : `Add ${label}`) : undefined}
            onClick={showClickableCards ? handleCardClick : undefined}
            onKeyDown={showClickableCards ? handleCardKeyDown : undefined}
        >
            {shouldShowRecommendation && (
                <div data-testid="recommendation-badge" className="sm:hidden md:flex lg:hidden">
                    {RecommendationBadge}
                </div>
            )}
            <CardHeader
                className={cn(
                    headerClasses,
                    `flex min-h-[150px] flex-row items-center justify-between border-b border-stroke-secondary px-3 py-4 sm:min-h-[112px] sm:px-5 md:min-h-[150px] lg:min-h-[112px]`,
                    showClickableCards && clickableCardsStyle
                )}
            >
                <div className={cn(`flex flex-col text-content-primary`, isGold && "my-9 mb-12 sm:my-3 sm:mb-4 md:my-10 md:mb-11 lg:my-7 lg:mb-0")}>
                    {shouldShowRecommendation && (
                        <div data-testid="recommendation-badge" className="hidden sm:flex md:hidden lg:flex">
                            {RecommendationBadge}
                        </div>
                    )}
                    <div
                        className={cn(
                            `flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2 md:flex-col md:items-start lg:flex-row lg:items-center lg:gap-2`,
                            showClickableCards && `gap-1`
                        )}
                    >
                        <Heading
                            level="h3"
                            className={cn(`order-1 font-bold sm:order-none md:order-1 lg:order-none`, isGold && showClickableCards && `mt-6 sm:mt-0 md:mt-7 lg:mt-0`)}
                        >
                            {label}
                        </Heading>
                        {showPopularAge && Number.isInteger(pet.age) && pet.species && !shouldShowRecommendation && (
                            <Badge variant="secondary" className={cn("w-fit")}>
                                Popular for {PolicyUtils.recommendedPreventiveText(pet.species, pet.age as number)}
                            </Badge>
                        )}
                    </div>
                    {typeof totalBenefitsValue === "number" && (
                        <div className={cn(`text-content-primary`, showClickableCards && `text-sm font-medium leading-5 text-content-secondary`)}>
                            Up to ${totalBenefitsValue}/yr in benefits
                        </div>
                    )}
                    {(sellingPoints?.length ?? 0) > 0 && (
                        <div
                            className={cn(
                                "mt-1 flex flex-col sm:flex-row sm:gap-2 md:flex-col md:gap-0 lg:flex-row lg:gap-2",
                                showClickableCards && `text-sm font-medium leading-5 text-content-secondary`
                            )}
                        >
                            {(sellingPoints ?? []).map((point, index) => (
                                <div key={index} className={cn(showClickableCards && `flex items-center`)}>
                                    <FontAwesomeIcon
                                        icon={showClickableCards ? faDiamond : faCircleCheck}
                                        className={cn(`mr-1 text-content-brand-b`, showClickableCards && `size-2 text-content-primary`)}
                                    />
                                    {point}
                                </div>
                            ))}
                        </div>
                    )}

                    {showClickableCards && (
                        <div className={cn(`mt-3 flex flex-row items-center gap-4`, isGold && `mt-9 sm:mt-4 md:mt-9 lg:mt-4`)}>
                            <PreventiveToggleButton label={label} isActive={isActive} tier={isGold ? "gold" : "platinum"} />
                            {priceLabelExperiment}
                        </div>
                    )}
                </div>
                <div>{image}</div>
            </CardHeader>
            <CardContent className={cn("flex flex-col items-center gap-2 text-content-primary", paddingStyles)}>
                {categorizePreventive ? (
                    <SectionGrid sections={benefits as SectionGridItem[]} isPlatinum={wellnessLevel === "wellness-platinum"} hasBreedRules={hasBreedRules} />
                ) : (
                    <PreventiveGrid
                        gridItems={benefits as IconGridItem[]}
                        wrapperClass={cn(columnClasses, "grid w-full", "gap-y-2 sm:gap-y-1 gap-x-1 sm:gap-x-3 md:gap-x-1 md:gap-y-2 lg:gap-x-3 lg:gap-y-2")}
                    />
                )}

                {/*  What's not covered - platinum recommendation test */}
                {categorizePreventive && isGold && isActive && showPlatinumBanner && <PlatinumRecommendationBanner onUpgrade={() => onChange("wellness-platinum")} />}
            </CardContent>
            {!showClickableCards && (
                <CardFooter className={cn("mt-auto flex items-center justify-between gap-2 rounded-b-3xl border px-3 py-5 sm:px-4", footerStyles)}>
                    {priceLabel}
                    {addControl}
                </CardFooter>
            )}
        </Card>
    );
}

export default PreventiveCard;
